<template>
  <div>
    <AlertSunshine :dialog="loadingDialog" :text="loadingText" />

    <v-card>
      <v-card-title>
        Historias de {{ WorkerName }}
        <v-spacer></v-spacer>
        <v-btn v-show="isDoctor || isNurse"
        color="primary" small @click="addHistory">
          <v-icon left>mdi-plus</v-icon> Agregar
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        @dblclick:row="handleRowDblClick"
        class="elevation-1"
        style="text-transform: uppercase"
      >
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            small
            @click="editHistory(item)"
            :title="'Editar historia'"
            v-if="canEdit(item)"
          >
            <v-icon color="orange" x-small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="viewPdf(item)" :title="'Ver ficha clínica'">
            <v-icon color="cyan">mdi-file-eye</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="printHistory(item)"
            :title="'Imprimir historia clínica'"
          >
            <v-icon color="purple">mdi-printer</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="viewAptitudePdf(item)"
            :title="'Ver PDF de Aptitud'"
            v-if="isDoctor"
          >
            <v-icon color="green">mdi-file-certificate</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="openHistoryChanges(item)"
            :title="'Ver historial de cambios'"
          >
            <v-icon color="blue darken-4" x-small>mdi-history</v-icon>
          </v-btn>
          <!-- Diálogo de PDF de Aptitud -->
          <v-dialog v-model="aptitudePdfDialog" max-width="500px">
            <v-card style="overflow: hidden !important; position: relative">
              <v-btn
                icon
                small
                style="position: absolute; top: 8px; right: 8px; z-index: 10"
                @click="aptitudePdfDialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <aptitude-card-pdf
                v-if="selectedHistoryForAptitudePdf"
                :item="selectedHistoryForAptitudePdf"
              />
            </v-card>
          </v-dialog>
          <v-btn
            v-show="item.StatusInd == 0 && isDoctor && item.MevStatus != 5"
            icon
            small
            elevation="0"
            @click="validateHistory(item)"
            :title="'Validar historia médica'"
          >
            <v-icon color="green darken-4" x-small
              >mdi mdi-check-decagram</v-icon
            >
          </v-btn>
          <!-- <v-btn
            v-show="item.StatusInd == 1"
            icon
            small
            elevation="0"
            @click="validateHistory(item)"
            :title="'Re-Validar historia médica'"
          >
            <v-icon color="green darken-4" x-small
              >mdi mdi-checkbox-multiple-marked-circle</v-icon
            >
          </v-btn> -->
        </template>
        <template v-slot:[`item.MevDate`]="{ item }">
          <v-chip x-small>
            {{ $fun.formatDateView(item.MevDate) }}
          </v-chip>
        </template>
        <template v-slot:[`item.MevStatus`]="{ item }">
          <v-chip :dark="true" x-small :color="item.MevStatusColor">
            {{ item.MevStatusName }}
          </v-chip>
        </template>
        <template v-slot:[`item.TypeDiagnosisDescription`]="{ item }">
          <ol v-if="item.TypeDiagnosisDescription" style="padding-left: 18px">
            <li
              v-for="(diag, idx) in item.TypeDiagnosisDescription.split(
                '||'
              ).filter((d) => d.trim())"
              :key="idx"
            >
              {{ diag.trim() }}
            </li>
          </ol>
          <span v-else>--</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo de edición/creación -->
    <v-dialog
      v-model="dialog"
      persistent
      fullscreen
      transition="dialog-bottom-transition"
      :retain-focus="false"
    >
      <v-card style="overflow: hidden !important">
        <history-edit-new
          v-if="dialog"
          :key="historyKey"
          @close="closeDialog"
          :pHistory="historyToEdit"
          :person="personData"
          :last-history="lastHistoryForTemplate"
        />
      </v-card>
    </v-dialog>

    <!-- Modal de indicaciones para validar historia -->
    <v-dialog v-model="indicationsDialog" max-width="650px" persistent>
      <v-card>
        <v-card-title>Indicaciones para la historia</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="indicationsText"
            label="Indicaciones"
            rows="4"
            auto-grow
            outlined
          />

          <v-row v-if="isRestrictionRequired" dense>
            <v-col cols="12" md="7">
              <s-select-definition
                :def="1537"
                v-model="validationRestrictionTimeMeasure"
                label="Medida de tiempo de restricción"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model.number="validationRestrictionTimeValue"
                label="Cantidad"
                type="number"
                min="1"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" @click="cancelIndications">Cancelar</v-btn>
          <v-btn color="success" @click="saveIndications">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de PDF -->
    <v-dialog
      v-model="pdfDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card style="overflow: hidden !important">
        <pdf-clinical-history
          v-if="selectedHistoryForPdf"
          ref="pdfComponent"
          :history="selectedHistoryForPdf"
          @close="pdfDialog = false"
        />
      </v-card>
    </v-dialog>

    <!-- Diálogo de historial de cambios -->
    <v-dialog v-model="historyChangesDialog" max-width="900px">
      <v-card>
        <v-toolbar dense flat>
          <v-toolbar-title>
            Historial de cambios
            <span v-if="selectedHistoryForChanges">
              - Ficha {{ selectedHistoryForChanges.MevDocumentNumber }}
            </span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="historyChangesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <history-changes-view
            v-if="selectedHistoryForChanges"
            :key="selectedHistoryForChanges.MevID"
            :mev-id="selectedHistoryForChanges.MevID"
            :current="selectedHistoryForChanges"
            @close="historyChangesDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <div
      style="position: fixed; left: -100000px; top: 0; width: 725px; opacity: 0; pointer-events: none"
    >
      <pdf-clinical-history
        v-if="selectedHistoryForServerSave"
        ref="validatedPdfSaver"
        :history="selectedHistoryForServerSave"
      />
    </div>
  </div>
</template>

<script>
import HistoryRecordService from "../../../services/HumanResource/HistoryRecordService";
import HistoryEditNew from "./HistoryEditNew.vue";
import HistoryChangesView from "./HistoryChangesView.vue";
import PdfClinicalHistory from "./PdfClinicalHistory.vue";
import AptitudeCardPdf from "@/components/HumanResource/OccupationalHealth/AptitudeCardPdf.vue";
import ParameterService from "@/services/General/ParameterService";
import AlertSunshine from "@/components/Utils/AlertSunshine.vue";
import swal from "sweetalert2";

export default {
  props: {
    WorkerName: String,
    PrsDocumentNumber: String,
    person: Object,
  },
  components: {
    HistoryEditNew,
    HistoryChangesView,
    PdfClinicalHistory,
    AptitudeCardPdf,
    AlertSunshine,
  },
  data() {
    return {
      dialog: false,
      historyToEdit: null,
      personData: null,
      historyKey: 0, // Para forzar re-render del componente hijo
      loading: false,
      items: [],
      historyChangesDialog: false,
      selectedHistoryForChanges: null,
      pdfDialog: false,
      selectedHistoryForPdf: null,
      selectedHistoryToValidate: null,
      indicationsDialog: false,
      indicationsText: "",
      validationRestrictionTimeMeasure: null,
      validationRestrictionTimeValue: null,
      aptitudePdfDialog: false,
      selectedHistoryForAptitudePdf: null,
      selectedHistoryForServerSave: null,
      loadingDialog: false,
      loadingText: "Procesando...",
      headers: [
        { text: "ID", value: "MevID" },
        { text: "Fecha", value: "MevDate" },
        { text: "Estado", value: "MevStatus" },
        { text: "Lev. Obs.", value: "MevLiftedObservation" },
        { text: "Diagnóstico", value: "TypeDiagnosisDescription" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    // Última historia (más reciente) para usarla como plantilla al crear una nueva
    lastHistoryForTemplate() {
      if (!this.items || this.items.length === 0) return null;
      const sorted = [...this.items].sort((a, b) => {
        return b.MevID - a.MevID;
      });
      return sorted[0];
    },
    isDoctor() {
      try {
        return (
          this.$fun.hasSpecialPermission &&
          this.$fun.hasSpecialPermission("DOCTOR")
        );
      } catch (e) {
        return false;
      }
    },
    isNurse() {
      try {
        return (
          this.$fun.hasSpecialPermission &&
          this.$fun.hasSpecialPermission("ENFERMERA")
        );
      } catch (e) {
        return false;
      }
    },
    isRestrictionRequired() {
      return Number(this.selectedHistoryToValidate?.MevStatus) === 2;
    },
  },
  mounted() {
    this.loadItems();
  },
  watch: {
    PrsDocumentNumber(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadItems();
      }
    },
  },
  methods: {
    viewAptitudePdf(item) {
      this.selectedHistoryForAptitudePdf = item;
      this.aptitudePdfDialog = true;
    },
    async loadItems() {
      if (!this.PrsDocumentNumber) return;
      this.loading = true;
      try {
        const response = await HistoryRecordService.listByWorker(
          this.PrsDocumentNumber
        );
        this.items = response.data;
      } catch (error) {
        console.error("Error loading histories", error);
        this.$fun &&
          this.$fun.alert &&
          this.$fun.alert("Error al cargar historias", "error");
      } finally {
        this.loading = false;
      }
    },

    canEdit(item) {
      if (item.StatusInd == 0) {
        return this.isDoctor || this.isNurse;
      }
      if (
        Number(item.StatusInd) === 1 &&
        this.isDoctor &&
        [2, 4].includes(Number(item.MevStatus))
      ) {
        return true;
      }
      return false;
    },

    addHistory() {
      this.historyToEdit = null;
      this.personData = this.person ? { ...this.person } : null;
      this.historyKey++; // Forzar re-render
      this.dialog = true;
    },

    viewPdf(item) {
      this.selectedHistoryForPdf = item;
      this.pdfDialog = true;
    },

    printHistory(item) {
      this.selectedHistoryForPdf = item;
      this.pdfDialog = true;
      // Esperar a que el diálogo se abra y el componente se monte
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.pdfComponent) {
            this.$refs.pdfComponent.printDirectly();
          }
        }, 800);
      });
    },

    handleRowDblClick(event, { item }) {
      this.editHistory(item);
    },

    editHistory(row) {
      console.log(this.isDoctor);
      
      // Validar permisos
      if (!this.canEdit(row)) {
        let message = "No tiene permisos para editar esta historia.";
        if (Number(row.StatusInd) === 2) {
          message = "La historia ya está enlazada a un contrato y no se puede editar.";
        } else if (Number(row.StatusInd) === 1 && !this.isDoctor) {
          message = "Solo el doctor puede editar historias clínicas validadas.";
        } else if (
          Number(row.StatusInd) === 1 &&
          this.isDoctor &&
          ![2, 4].includes(Number(row.MevStatus))
        ) {
          message =
            "Solo se puede re-editar una historia validada en estado Apto con restricción u Observado.";
        } else if (Number(row.StatusInd) === 1) {
          message = "La historia ya está validada y no se puede editar.";
        }
        this.$fun && this.$fun.alert && this.$fun.alert(message, "warning");
        return;
      }
      //validar que la fecha de la historia no sea mayor por ejemplo a 30 dias, para evitar editar historias muy antiguas
      const historyDate = new Date(row.MevDate);
      const today = new Date();
      const diffTime = Math.abs(today - historyDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 30 && Number(row.StatusInd) !== 1) {
        this.$fun &&
          this.$fun.alert &&
          this.$fun.alert(
            "No se pueden editar historias con fecha mayor a 30 días.",
            "warning"
          );
        return;
      }

      

      // Clonar profundamente el objeto para evitar mutaciones
      this.historyToEdit = JSON.parse(JSON.stringify(row));
      this.personData = this.person ? { ...this.person } : null;
      this.historyKey++; // Forzar re-render del componente hijo
      this.dialog = true;
    },

    openHistoryChanges(row) {
      this.selectedHistoryForChanges = row;
      this.historyChangesDialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.historyToEdit = null;
      this.personData = null;
      // Recargar la lista después de cerrar
      this.loadItems();
    },

    validateHistory(item) {
      if (!this.isDoctor) {
        this.$fun &&
          this.$fun.alert &&
          this.$fun.alert(
            "Solo el doctor puede validar una historia.",
            "warning"
          );
        return;
      }
      this.selectedHistoryToValidate = item;
      this.indicationsDialog = true;
      this.indicationsText = item.MevIndications || "";
      this.validationRestrictionTimeMeasure = item.TypeRestrictionTimeMeasure;
      this.validationRestrictionTimeValue = item.RestrictionTimeValue;
    },

    async waitForValidatedPdfSaver(maxAttempts = 10) {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await this.$nextTick();

        const saverRef = Array.isArray(this.$refs.validatedPdfSaver)
          ? this.$refs.validatedPdfSaver[0]
          : this.$refs.validatedPdfSaver;

        if (saverRef?.savePdfToServer) {
          return saverRef;
        }

        await new Promise((resolve) => window.requestAnimationFrame(resolve));
      }

      return null;
    },

    async uploadValidatedHistoryPdf(history) {
      if (!history) {
        return;
      }

      this.loadingText = "Subiendo PDF...";

      const routeConfig = await ParameterService.search(
        { PrmName: "Route_ValidateHistoryRRHH" },
        this.$fun.getUserID()
      );
      const configuredPath = routeConfig?.data?.PrmValue || "";

      if (!configuredPath) {
        throw new Error("No existe una ruta configurada para el parámetro Route_ValidateHistoryRRHH.");
      }

      try {
        this.selectedHistoryForServerSave = JSON.parse(JSON.stringify(history));

        const pdfSaver = await this.waitForValidatedPdfSaver();
        if (!pdfSaver?.savePdfToServer) {
          throw new Error("No se pudo inicializar el generador de PDF de historia clínica.");
        }

        await pdfSaver.savePdfToServer("Route_ValidateHistoryRRHH");
        return configuredPath;
      } finally {
        this.selectedHistoryForServerSave = null;
      }
    },

    async showManualPdfUploadModal(history) {
      const response = await swal.fire({
        title: "Historia validada",
        text: "La historia fue validada correctamente, pero el PDF no se pudo subir a la carpeta configurada. Puede descargarlo manualmente desde el sistema y cargarlo en la ruta correspondiente.",
        icon: "warning",
        allowOutsideClick: false
      });

      if (response.isConfirmed && history) {
        this.viewPdf(JSON.parse(JSON.stringify(history)));
      }
    },

    async saveIndications() {
      if (!this.selectedHistoryToValidate) return;

      try {
        const confirmation = await this.$fun.alert(
          "¿Desea validar con las siguientes indicaciones?",
          "question"
        );

        if (!confirmation.value) return;

        this.loadingDialog = true;
        this.loadingText = "Validando historia...";

        if (this.isRestrictionRequired) {
          if (!this.validationRestrictionTimeMeasure) {
            this.$fun.alert(
              "Debe seleccionar la medida de tiempo de restricción para validar una historia Apto con restricción",
              "warning"
            );
            this.loadingDialog = false;
            this.loadingText = "Procesando...";
            return;
          }

          if (
            !Number.isInteger(Number(this.validationRestrictionTimeValue)) ||
            Number(this.validationRestrictionTimeValue) <= 0
          ) {
            this.$fun.alert(
              "Debe ingresar una cantidad de restricción entera mayor a 0",
              "warning"
            );
            this.loadingDialog = false;
            this.loadingText = "Procesando...";
            return;
          }
        }

        const obj = {
          MevID: this.selectedHistoryToValidate.MevID,
          MevStatus:this.selectedHistoryToValidate.MevStatus , 
          MevIndications: this.indicationsText,
          DocValidateID: this.$fun.getUserID(),
          TypeRestrictionTimeMeasure: this.isRestrictionRequired
            ? this.validationRestrictionTimeMeasure
            : null,
          RestrictionTimeValue: this.isRestrictionRequired
            ? Number(this.validationRestrictionTimeValue)
            : null,
        };

        await HistoryRecordService.validateHistory(obj, this.$fun.getUserID());

        this.indicationsDialog = false;
        this.indicationsText = "";
        this.validationRestrictionTimeMeasure = null;
        this.validationRestrictionTimeValue = null;

        await this.loadItems();

        const validatedHistory =
          this.items.find((item) => item.MevID === obj.MevID) ||
          JSON.parse(JSON.stringify(this.selectedHistoryToValidate));

        if ([1, 2,3,4].includes(Number(validatedHistory?.MevStatus))) {
          try {
            const configuredPath = await this.uploadValidatedHistoryPdf(validatedHistory);
            this.$fun.alert(
              `PDF guardado en la ruta configurada del servidor: ${configuredPath}`,
              "success"
            );
          } catch (pdfError) {
            console.error("Error al generar/subir PDF validado:", pdfError);
            await this.showManualPdfUploadModal(validatedHistory);
          }
        }

        this.selectedHistoryToValidate = null;

        this.$fun.alert(
          "Indicaciones guardadas y historia validada correctamente",
          "success"
        );
      } catch (error) {
        console.error("Error al validar historia:", error);
        this.$fun.alert("Error al guardar indicaciones", "error");
      } finally {
        this.loadingDialog = false;
        this.loadingText = "Procesando...";
      }
    },

    cancelIndications() {
      this.indicationsDialog = false;
      this.selectedHistoryToValidate = null;
      this.indicationsText = "";
      this.validationRestrictionTimeMeasure = null;
      this.validationRestrictionTimeValue = null;
    },
  },
};
</script>